// Phone scan (screen 7): reads a retail barcode or QR code with the camera
// and opens the phone report. Uses the browser's BarcodeDetector where it
// exists (Chrome on Android) and the zxing-wasm ponyfill elsewhere (Safari).
// Bundled to public/js/scan.js by `npm run build:client`.

import { BarcodeDetector as Ponyfill, prepareZXingModule } from "barcode-detector/ponyfill";
import { queryFromScan } from "../check/codes";

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "itf", "qr_code", "data_matrix"] as const;

type Detector = { detect(source: HTMLVideoElement): Promise<{ rawValue: string; format: string }[]> };

const video = document.getElementById("camera") as HTMLVideoElement;
const start = document.getElementById("scan-start") as HTMLButtonElement;
const status = document.getElementById("scan-status")!;
const typeSelect = document.getElementById("ptype") as HTMLSelectElement;

async function makeDetector(): Promise<Detector> {
  const Native = (globalThis as { BarcodeDetector?: { new (o: object): Detector; getSupportedFormats(): Promise<string[]> } }).BarcodeDetector;
  if (Native) {
    const supported = await Native.getSupportedFormats().catch((): string[] => []);
    if (["ean_13", "upc_a", "qr_code"].every((f) => supported.includes(f))) {
      return new Native({ formats: FORMATS.filter((f) => supported.includes(f)) });
    }
  }
  // Serve the decoder from our own origin rather than a CDN.
  prepareZXingModule({ overrides: { locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? "/vendor/zxing_reader.wasm" : prefix + path) } });
  return new Ponyfill({ formats: [...FORMATS] }) as unknown as Detector;
}

function stop() {
  for (const track of (video.srcObject as MediaStream | null)?.getTracks() ?? []) track.stop();
  video.srcObject = null;
}

async function scan() {
  start.disabled = true;
  status.textContent = "Starting the camera…";
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
  } catch {
    status.textContent = "The camera isn't available. Type the GTIN below instead.";
    start.disabled = false;
    return;
  }
  video.srcObject = stream;
  await video.play();
  document.getElementById("viewfinder")!.classList.add("live");
  status.textContent = "Hold the barcode inside the frame";
  start.hidden = true;

  const detector = await makeDetector();
  while (video.srcObject) {
    try {
      const [found] = await detector.detect(video);
      if (found) {
        const query = queryFromScan(found.rawValue, found.format);
        if (query.ok) {
          status.textContent = "Got it. Checking…";
          navigator.vibrate?.(60);
          stop();
          location.href = `/check/phone?${new URLSearchParams({ q: query.q, type: typeSelect.value })}`;
          return;
        }
        status.textContent = query.reason;
      }
    } catch {
      // A frame that can't be decoded yet; keep going.
    }
    await new Promise((r) => setTimeout(r, 200));
  }
}

start.addEventListener("click", scan);
addEventListener("pagehide", stop);
